<template>
  <div class="crm-mapping-editor">
    <div class="tw-max-w-7xl tw-mx-auto tw-p-6">
      <!-- Header -->
      <div class="tw-mb-6">
        <h2 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
          CRM Mapping Editor
        </h2>
        <p class="tw-text-gray-600">
          Map dossier template fields to {{ crmType.toUpperCase() }} fields
        </p>
      </div>

      <!-- Loading States -->
      <div v-if="isLoading" class="tw-text-center tw-py-8">
        <i class="fas fa-spinner fa-spin tw-text-2xl tw-text-gray-400"></i>
        <p class="tw-text-gray-600 tw-mt-2">Loading field schemas...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mb-6">
        <div class="tw-flex tw-items-center">
          <i class="fas fa-exclamation-triangle tw-text-red-500 tw-mr-2"></i>
          <p class="tw-text-red-700">{{ error }}</p>
        </div>
      </div>

      <!-- Mapping Interface -->
      <div v-else class="tw-space-y-6">
        
        <!-- Current Mappings Header -->
        <div class="tw-flex tw-items-center tw-justify-between">
          <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900">
            Field Mappings ({{ currentMappings.length }})
          </h3>
          <div>
            <button 
              @click="saveAllMappings"
              class="btn btn-primary tw-px-6 tw-py-3 tw-text-base tw-font-semibold tw-shadow-lg"
              :disabled="isSaving || currentMappings.length === 0"
            >
              <i v-if="isSaving" class="fas fa-spinner fa-spin tw-mr-2"></i>
              <i v-else class="fas fa-save tw-mr-2"></i>
              Save All Mappings
            </button>
          </div>
        </div>

        <!-- Mapping Table -->
        <div class="tw-bg-white tw-rounded-lg tw-shadow tw-overflow-hidden">
          <div class="tw-grid tw-grid-cols-12 tw-gap-0 tw-bg-gray-50 tw-border-b tw-border-gray-200">
            <!-- Headers -->
            <div class="tw-col-span-5 tw-px-4 tw-py-3 tw-border-r tw-border-gray-200">
              <h4 class="tw-text-sm tw-font-medium tw-text-gray-900">
                Dossier Field
              </h4>
            </div>
            <div class="tw-col-span-5 tw-px-4 tw-py-3 tw-border-r tw-border-gray-200">
              <h4 class="tw-text-sm tw-font-medium tw-text-gray-900">
                {{ crmType.toUpperCase() }} Field
              </h4>
            </div>
            <div class="tw-col-span-2 tw-px-4 tw-py-3">
              <h4 class="tw-text-sm tw-font-medium tw-text-gray-900 tw-text-center">
                Actions
              </h4>
            </div>
          </div>

          <!-- Existing Mappings -->
          <div v-for="(mapping, index) in currentMappings" :key="index" 
               class="tw-grid tw-grid-cols-12 tw-gap-0 tw-border-b tw-border-gray-100 hover:tw-bg-gray-50">
            
            <!-- Dossier Field Column -->
            <div class="tw-col-span-5 tw-px-4 tw-py-3 tw-border-r tw-border-gray-200">
              <div class="input-container">
                <input 
                  v-model="mapping.dossierSearchTerm"
                  type="text"
                  placeholder="Search dossier fields..."
                  class="mapping-search-input"
                  @focus="setDossierFocus(index, true)"
                  @blur="setDossierFocus(index, false)"
                  @keydown.enter="selectFirstDossierMatchForRow(index)"
                >
                <i class="fas fa-search search-icon"></i>
              </div>
              
              <!-- Dossier Field Dropdown for existing mapping -->
              <div v-if="mapping.dossierFocused && mapping.dossierSearchTerm && getFilteredDossierFieldsForRow(index).length > 0" 
                   class="mapping-dropdown">
                <div 
                  v-for="field in getFilteredDossierFieldsForRow(index).slice(0, 10)" 
                  :key="field.path"
                  @click="selectDossierFieldForRow(index, field)"
                  class="mapping-dropdown-item"
                >
                  <div class="mapping-item-title">{{ prettifyMixedCase(field.path) }}</div>
                  <div class="mapping-item-subtitle">{{ field.crm_relevance }}</div>
                  <div v-if="getSampleValue(field.path)" class="mapping-item-sample">
                    Sample: {{ getSampleValue(field.path) }}
                  </div>
                </div>
              </div>
              
              <div v-if="mapping.dossier_field_path" class="tw-text-xs tw-text-gray-500 tw-mt-1">
                {{ getDossierFieldContext(mapping.dossier_field_path) }}
              </div>
            </div>

            <!-- CRM Field Column -->
            <div class="tw-col-span-5 tw-px-4 tw-py-3 tw-border-r tw-border-gray-200">
              <div class="input-container">
                <input 
                  v-model="mapping.crmSearchTerm"
                  type="text"
                  placeholder="Search CRM fields..."
                  class="mapping-search-input"
                  @focus="setCrmFocus(index, true)"
                  @blur="setCrmFocus(index, false)"
                  @keydown.enter="selectFirstCrmMatchForRow(index)"
                >
                <i class="fas fa-search search-icon"></i>
              </div>
              
              <!-- CRM Field Dropdown for existing mapping -->
              <div v-if="mapping.crmFocused && mapping.crmSearchTerm && getFilteredCrmFieldsForRow(index).length > 0" 
                   class="mapping-dropdown">
                <div 
                  v-for="field in getFilteredCrmFieldsForRow(index).slice(0, 10)" 
                  :key="field.display_name"
                  @click="selectCrmFieldForRow(index, field)"
                  class="mapping-dropdown-item"
                >
                  <div class="mapping-item-title">
                    {{ field.display_name }}
                    <span v-if="field.required" class="tw-text-red-500 tw-ml-1">*</span>
                  </div>
                  <div class="mapping-item-subtitle">{{ field.description || 'No description' }}</div>
                  <div class="mapping-item-sample">Type: {{ field.field_type }}</div>
                </div>
              </div>
              
              <div v-if="mapping.crm_field_name" class="tw-text-xs tw-text-gray-500 tw-mt-1">
                {{ getCrmFieldDescription(mapping.crm_field_name) }}
              </div>
            </div>

            <!-- Actions Column -->
            <div class="tw-col-span-2 tw-flex tw-pt-4">
              <button 
                @click="removeMapping(index)"
                class="tw-text-red-600 hover:tw-text-red-800 tw-text-sm tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-border-transparent tw-bg-transparent"
                title="Remove mapping"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- Add New Mapping Row -->
          <div class="tw-grid tw-grid-cols-12 tw-gap-0 tw-border-b tw-border-gray-100 tw-bg-blue-50">
            
            <!-- Dossier Field Search -->
            <div class="tw-col-span-5 tw-px-4 tw-py-3 tw-border-r tw-border-gray-200">
              <div class="input-container">
                <input 
                  v-model="newMapping.dossierSearchTerm"
                  type="text"
                  placeholder="Search dossier fields..."
                  class="mapping-search-input"
                  @input="filterDossierFields"
                  @focus="newMapping.dossierFocused = true"
                  @blur="setTimeout(() => newMapping.dossierFocused = false, 150)"
                  @keydown.enter="selectFirstDossierMatch"
                >
                <i class="fas fa-search search-icon"></i>
              </div>
              
              <!-- Dossier Field Dropdown -->
              <div v-if="newMapping.dossierFocused && newMapping.dossierSearchTerm && filteredDossierFieldsForSearch.length > 0" 
                   class="mapping-dropdown">
                <div 
                  v-for="field in filteredDossierFieldsForSearch.slice(0, 10)" 
                  :key="field.path"
                  @click="selectDossierField(field)"
                  class="mapping-dropdown-item"
                >
                  <div class="mapping-item-title">{{ prettifyMixedCase(field.path) }}</div>
                  <div class="mapping-item-subtitle">{{ field.crm_relevance }}</div>
                  <div v-if="getSampleValue(field.path)" class="mapping-item-sample">
                    Sample: {{ getSampleValue(field.path) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- CRM Field Search -->
            <div class="tw-col-span-5 tw-px-4 tw-py-3 tw-border-r tw-border-gray-200">
              <div class="input-container">
                <input 
                  v-model="newMapping.crmSearchTerm"
                  type="text"
                  placeholder="Search fields..."
                  class="mapping-search-input"
                  @input="filterCrmFields"
                  @focus="newMapping.crmFocused = true"
                  @blur="setTimeout(() => newMapping.crmFocused = false, 150)"
                  @keydown.enter="selectFirstCrmMatch"
                >
                <i class="fas fa-search search-icon"></i>
              </div>
              
              <!-- CRM Field Dropdown -->
              <div v-if="newMapping.crmFocused && newMapping.crmSearchTerm && filteredCrmFieldsForSearch.length > 0" 
                   class="mapping-dropdown">
                <div 
                  v-for="field in filteredCrmFieldsForSearch.slice(0, 10)" 
                  :key="field.display_name"
                  @click="selectCrmField(field)"
                  class="mapping-dropdown-item"
                >
                  <div class="mapping-item-title">
                    {{ field.display_name }}
                    <span v-if="field.required" class="tw-text-red-500 tw-ml-1">*</span>
                  </div>
                  <div class="mapping-item-subtitle">{{ field.description || 'No description' }}</div>
                  <div class="mapping-item-sample">Type: {{ field.field_type }}</div>
                </div>
              </div>
            </div>

            <!-- Add Button Column -->
            <div class="tw-col-span-2 tw-px-4 tw-py-3 tw-flex tw-justify-center tw-items-center">
              <button 
                @click="addMapping"
                :disabled="!newMapping.dossierField || !newMapping.crmField"
                class="tw-bg-blue-600 tw-text-white tw-rounded-full tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center hover:tw-bg-blue-700 disabled:tw-bg-gray-400 disabled:tw-cursor-not-allowed"
                title="Add mapping"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-4">
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-text-center">
            <div>
              <div class="tw-text-2xl tw-font-bold tw-text-blue-600">
                {{ dossierFields.length }}
              </div>
              <div class="tw-text-sm tw-text-gray-600">Available Dossier Fields</div>
            </div>
            <div>
              <div class="tw-text-2xl tw-font-bold tw-text-green-600">
                {{ crmFields.length }}
              </div>
              <div class="tw-text-sm tw-text-gray-600">Available {{ crmType.toUpperCase() }} Fields</div>
            </div>
            <div>
              <div class="tw-text-2xl tw-font-bold tw-text-purple-600">
                {{ currentMappings.length }}
              </div>
              <div class="tw-text-sm tw-text-gray-600">Current Mappings</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import { prettifyMixedCase } from '@frontend/shared/Util.js';

export default {
  name: 'CrmMappingEditor',
  
  props: {
    crmType: {
      type: String,
      default: 'salesforce'
    },
    initialMapping: {
      type: Object,
      default: null
    }
  },
  
  data() {
    return {
      isLoading: true,
      isSaving: false,
      error: null,
      
      // Field data
      dossierFields: [],
      crmFields: [],
      sampleValues: {},
      
      // Current mappings
      currentMappings: [],
      
      // New mapping state
      newMapping: {
        dossierSearchTerm: '',
        crmSearchTerm: '',
        dossierField: null,
        crmField: null,
        dossierFocused: false,
        crmFocused: false,
      },
      
      // Filtered fields for dropdowns
      filteredDossierFields: [],
      filteredCrmFields: [],
      filteredDossierFieldsForSearch: [],
      filteredCrmFieldsForSearch: [],
    };
  },
  
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    }
  },
  
  watch: {
    initialMapping: {
      handler(newMapping) {
        if (newMapping) {
          this.loadCurrentMapping();
        } else {
          this.currentMappings = [];
        }
      },
      immediate: true
    }
  },
  
  async mounted() {
    await this.initialize();
  },
  
  methods: {
    async initialize() {
      this.isLoading = true;
      this.error = null;
      
      try {
        // Load both dossier and CRM fields in parallel
        await Promise.all([
          this.loadDossierFields(),
          this.loadCrmFields()
        ]);
        
        // Load current mapping if available
        await this.loadCurrentMapping();
        
      } catch (error) {
        this.error = `Failed to initialize: ${error.message}`;
        console.error('Initialization error:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadDossierFields() {
      try {
        const response = await fetchWrapper('/api/v1/crm/get-dossier-fields/', {
          method: 'GET',
          headers: this.headers,
        });
        
        if (response.ok) {
          const data = await response.json();
          this.dossierFields = data.fields;
          this.sampleValues = data.sample_values || {};
          this.filteredDossierFields = [...this.dossierFields];
        } else {
          throw new Error('Failed to load dossier template fields');
        }
      } catch (error) {
        console.error('Error loading dossier template fields:', error);
        throw error;
      }
    },
    
    async loadCrmFields() {
      try {
        const response = await fetchWrapper(`/api/v1/crm/get-crm-fields/?crm_type=${this.crmType}`, {
          method: 'GET',
          headers: this.headers,
        });
        
        if (response.ok) {
          const data = await response.json();
          this.crmFields = data.fields;
          this.filteredCrmFields = [...this.crmFields];
        } else {
          throw new Error('Failed to load CRM fields');
        }
      } catch (error) {
        console.error('Error loading CRM fields:', error);
        throw error;
      }
    },
    
    async loadCurrentMapping() {
      if (this.initialMapping && this.initialMapping.object_mappings) {
        // Convert from the mapping format to our editor format
        this.currentMappings = [];
        
        for (const objMapping of this.initialMapping.object_mappings) {
          for (const fieldMapping of objMapping.fields) {
            this.currentMappings.push({
              dossier_field_path: fieldMapping.dossier_field_path,
              crm_field_name: `${objMapping.object_type}.${fieldMapping.crm_field_name}`,
              object_type: objMapping.object_type,
              dossierSearchTerm: this.prettifyMixedCase(fieldMapping.dossier_field_path),
              crmSearchTerm: `${objMapping.object_type}.${fieldMapping.crm_field_name}`,
              dossierFocused: false,
              crmFocused: false,
            });
          }
        }
      } else {
        this.currentMappings = [];
      }
    },
    
    filterDossierFields() {
      const searchTerm = this.newMapping.dossierSearchTerm.toLowerCase();
      if (!searchTerm) {
        this.filteredDossierFieldsForSearch = [];
        return;
      }
      
      this.filteredDossierFieldsForSearch = this.dossierFields.filter(field => 
        field.path.toLowerCase().includes(searchTerm) ||
        this.prettifyMixedCase(field.path).toLowerCase().includes(searchTerm) ||
        field.crm_relevance.toLowerCase().includes(searchTerm) ||
        field.description.toLowerCase().includes(searchTerm)
      );
    },
    
    filterCrmFields() {
      const searchTerm = this.newMapping.crmSearchTerm.toLowerCase();
      if (!searchTerm) {
        this.filteredCrmFieldsForSearch = [];
        return;
      }
      
      this.filteredCrmFieldsForSearch = this.crmFields.filter(field => 
        field.display_name.toLowerCase().includes(searchTerm) ||
        field.field_name.toLowerCase().includes(searchTerm) ||
        (field.description && field.description.toLowerCase().includes(searchTerm))
      );
    },
    
    selectDossierField(field) {
      this.newMapping.dossierField = field;
      this.newMapping.dossierSearchTerm = this.prettifyMixedCase(field.path);
      this.newMapping.dossierFocused = false; // Close dropdown
      this.filteredDossierFieldsForSearch = [];
    },
    
    selectCrmField(field) {
      this.newMapping.crmField = field;
      this.newMapping.crmSearchTerm = field.display_name;
      this.newMapping.crmFocused = false; // Close dropdown
      this.filteredCrmFieldsForSearch = [];
    },
    
    selectFirstDossierMatch() {
      if (this.filteredDossierFieldsForSearch.length > 0) {
        this.selectDossierField(this.filteredDossierFieldsForSearch[0]);
      }
    },
    
    selectFirstCrmMatch() {
      if (this.filteredCrmFieldsForSearch.length > 0) {
        this.selectCrmField(this.filteredCrmFieldsForSearch[0]);
      }
    },
    
    addMapping() {
      if (!this.newMapping.dossierField || !this.newMapping.crmField) return;
      
      // Check if mapping already exists
      const exists = this.currentMappings.some(mapping => 
        mapping.dossier_field_path === this.newMapping.dossierField.path &&
        mapping.crm_field_name === this.newMapping.crmField.display_name
      );
      
      if (exists) {
        this.$oruga.notification.open({
          message: 'This mapping already exists',
          variant: 'warning',
        });
        return;
      }
      
      // Add the new mapping
      this.currentMappings.push({
        dossier_field_path: this.newMapping.dossierField.path,
        crm_field_name: this.newMapping.crmField.display_name,
        object_type: this.newMapping.crmField.object_type,
        dossierSearchTerm: this.prettifyMixedCase(this.newMapping.dossierField.path), // Initialize with prettified field name
        crmSearchTerm: this.newMapping.crmField.display_name, // Initialize with display name
        dossierFocused: false,
        crmFocused: false,
      });
      
      // Clear the form
      this.newMapping = {
        dossierSearchTerm: '',
        crmSearchTerm: '',
        dossierField: null,
        crmField: null,
        dossierFocused: false,
        crmFocused: false,
      };
      this.filteredDossierFieldsForSearch = [];
      this.filteredCrmFieldsForSearch = [];
      
      this.$oruga.notification.open({
        message: 'Mapping added successfully',
        variant: 'success',
        duration: 2000
      });
    },
    
    removeMapping(index) {
      this.currentMappings.splice(index, 1);
      
      this.$oruga.notification.open({
        message: 'Mapping removed',
        variant: 'info',
        duration: 2000,
      });
    },
    
    async saveAllMappings() {
      if (this.currentMappings.length === 0) {
        this.$oruga.notification.open({
          message: 'No mappings to save',
          variant: 'warning',
        });
        return;
      }
      
      this.isSaving = true;
      
      try {
        const response = await fetchWrapper('/api/v1/crm/save-mapping-edits/', {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({
            crm_type: this.crmType,
            mappings: this.currentMappings
          }),
        });
        
        if (response.ok) {
          const result = await response.json();
          
          this.$oruga.notification.open({
            message: `Successfully saved ${result.total_mappings} mappings for ${result.object_types.length} object types`,
            variant: 'success',
            duration: 5000
          });
          
          // Emit event to parent component
          this.$emit('mapping-saved', {
            mappingKey: result.mapping_key,
            totalMappings: result.total_mappings,
            objectTypes: result.object_types
          });
          
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save mappings');
        }
        
      } catch (error) {
        this.$oruga.notification.open({
          message: `Error saving mappings: ${error.message}`,
          variant: 'danger',
        });
        console.error('Error saving mappings:', error);
      } finally {
        this.isSaving = false;
      }
    },
    
    getSampleValue(path) {
      return this.sampleValues[path] || '';
    },
    
    getDossierFieldContext(path) {
      const field = this.dossierFields.find(f => f.path === path);
      return field ? field.crm_relevance : '';
    },
    
    getCrmFieldDescription(displayName) {
      const field = this.crmFields.find(f => f.display_name === displayName);
      return field ? (field.description || `Type: ${field.field_type}`) : '';
    },
    
    selectFirstDossierMatchForRow(index) {
      const filteredFields = this.getFilteredDossierFieldsForRow(index);
      if (filteredFields.length > 0) {
        this.selectDossierFieldForRow(index, filteredFields[0]);
      }
    },
    
    selectFirstCrmMatchForRow(index) {
      const filteredFields = this.getFilteredCrmFieldsForRow(index);
      if (filteredFields.length > 0) {
        this.selectCrmFieldForRow(index, filteredFields[0]);
      }
    },
    
    selectDossierFieldForRow(index, field) {
      if (!field) return;
      const mapping = this.currentMappings[index];
      mapping.dossier_field_path = field.path;
      mapping.dossierSearchTerm = this.prettifyMixedCase(field.path); // Set search term to show the selected field
      mapping.dossierFocused = false; // Close dropdown
    },
    
    selectCrmFieldForRow(index, field) {
      if (!field) return;
      const mapping = this.currentMappings[index];
      mapping.crm_field_name = field.display_name;
      mapping.crmSearchTerm = field.display_name; // Set search term to show the selected field
      mapping.crmFocused = false; // Close dropdown
      
      // Extract object type from field
      mapping.object_type = field.object_type;
    },
    
    getFilteredDossierFieldsForRow(index) {
      const mapping = this.currentMappings[index];
      if (!mapping.dossierSearchTerm || mapping.dossierSearchTerm.length < 1) {
        return [];
      }
      
      const searchTerm = mapping.dossierSearchTerm.toLowerCase();
      return this.dossierFields.filter(field => 
        field.path.toLowerCase().includes(searchTerm) ||
        this.prettifyMixedCase(field.path).toLowerCase().includes(searchTerm) ||
        field.crm_relevance.toLowerCase().includes(searchTerm) ||
        field.description.toLowerCase().includes(searchTerm)
      );
    },
    
    getFilteredCrmFieldsForRow(index) {
      const mapping = this.currentMappings[index];
      if (!mapping.crmSearchTerm || mapping.crmSearchTerm.length < 1) {
        return [];
      }
      
      const searchTerm = mapping.crmSearchTerm.toLowerCase();
      return this.crmFields.filter(field => 
        field.display_name.toLowerCase().includes(searchTerm) ||
        field.field_name.toLowerCase().includes(searchTerm) ||
        (field.description && field.description.toLowerCase().includes(searchTerm))
      );
    },
    
    setDossierFocus(index, focused) {
      const mapping = this.currentMappings[index];
      if (focused) {
        mapping.dossierFocused = focused;
      } else {
        // Add delay to allow click events to register before closing dropdown
        setTimeout(() => {
          mapping.dossierFocused = false;
        }, 150);
      }
    },
    
    setCrmFocus(index, focused) {
      const mapping = this.currentMappings[index];
      if (focused) {
        mapping.crmFocused = focused;
      } else {
        // Add delay to allow click events to register before closing dropdown
        setTimeout(() => {
          mapping.crmFocused = false;
        }, 150);
      }
    },
    
    prettifyMixedCase
  }
};
</script>

<style scoped>
.crm-mapping-editor {
  min-height: 100vh;
  background-color: #f9fafb;
}

.btn {
  @apply tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-border tw-text-sm tw-font-medium tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2;
}

.btn-primary {
  @apply tw-border-transparent tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-ring-blue-500;
}

/* .btn-outline-secondary {
  @apply tw-border-gray-300 tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 focus:tw-ring-gray-500;
} */

.btn-sm {
  @apply tw-px-2 tw-py-1 tw-text-xs;
}

.btn:disabled {
  @apply tw-opacity-50 tw-cursor-not-allowed;
}

/* Make dropdowns appear above other elements */
.tw-absolute.tw-z-10 {
  z-index: 1000;
}

/* Enhanced input styling to match StakeUI components */
.mapping-search-input {
  background: var(--stk-background);
  border: 1px solid var(--stk-border);
  border-radius: 0.375rem;
  color: var(--stk-text);
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  width: 100%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.mapping-search-input:focus {
  border-color: var(--stk-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  outline: none;
}

.mapping-search-input::placeholder {
  color: var(--stk-text-weak);
}

/* Search icon positioning */
.search-icon {
  color: var(--stk-text-weak);
  left: 0.75rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

/* Enhanced dropdown styling to match MenuList */
.mapping-dropdown {
  background: var(--stk-background);
  border: 1px solid var(--stk-border);
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  margin-top: 0.25rem;
  max-height: 15rem;
  overflow-y: auto;
  position: absolute;
  width: 20rem;
  z-index: 1000;
}

/* Enhanced dropdown item styling to match MenuItem */
.mapping-dropdown-item {
  border-bottom: 1px solid var(--stk-border-light);
  cursor: pointer;
  padding: 0.75rem 1rem;
  transition: background-color 0.15s ease-in-out;
}

.mapping-dropdown-item:last-child {
  border-bottom: none;
}

.mapping-dropdown-item:hover {
  background-color: var(--stk-background-alt);
}

.mapping-dropdown-item:active {
  background-color: var(--stk-background-strong);
}

/* Dropdown item content styling */
.mapping-item-title {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--stk-text);
  margin-bottom: 0.125rem;
}

.mapping-item-subtitle {
  font-size: 0.75rem;
  color: var(--stk-text-weak);
  margin-bottom: 0.125rem;
}

.mapping-item-sample {
  font-size: 0.75rem;
  color: var(--stk-primary);
}

/* Input container styling */
.input-container {
  position: relative;
  width: 100%;
}

/* Scrollbar styling for dropdowns */
.mapping-dropdown::-webkit-scrollbar {
  width: 0.375rem;
}

.mapping-dropdown::-webkit-scrollbar-track {
  background: var(--stk-background-alt);
  border-radius: 0.375rem;
}

.mapping-dropdown::-webkit-scrollbar-thumb {
  background: var(--stk-border);
  border-radius: 0.375rem;
}

.mapping-dropdown::-webkit-scrollbar-thumb:hover {
  background: var(--stk-text-weak);
}
</style> 
